import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {Steps} from "primereact/steps";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import {CONTENT_TYPE_UIDS} from "../../../system/content-type-registry";
import {hasAdminPermission} from "../../../system/admin-access";
import {useContentTypeData} from "../../../system/use-content-type-data";
import {useTranslate} from "../../../system/useTranslate";
import MarkdownHelp from "../../../system/components/markdown-help";
import BillingCollectionTable, {dateColumn, moneyColumn, relationColumn, statusColumn} from "../components/billing-collection-table";
import {
    ClearableDropdown,
    DataState,
    FormDialog,
    FormField,
    RTL_DATATABLE_PROPS,
    RTL_DROPDOWN_PROPS,
    SalesMultiSelect,
    SalesSectionHeader,
    SwitchField,
    TableCard,
} from "../components/common";
import {entityKey, formatMoney, relationMutation, relationMutationMany, statusSeverity, toDateTimeInput, toISOStringOrNull} from "../sales-utils";

const EMPTY = {
    code: "",
    title: "",
    source: "manual",
    discountCodeStatus: "draft",
    discountType: "percentage",
    value: 0,
    currency: "IRR",
    maximumDiscountAmount: null,
    startsAt: "",
    endsAt: "",
    totalUsageLimit: null,
    perWorkspaceLimit: null,
    perUserLimit: null,
    minimumOrderAmount: null,
    minimumBillingPeriods: 0,
    firstPurchaseOnly: false,
    newWorkspaceOnly: false,
    eligiblePlans: [],
    eligiblePrices: [],
    excludedPlans: [],
    eligibleItemTypes: [],
    ownerUser: null,
    ownerWorkspace: null,
    stackable: false,
    priority: 0,
    autoApply: false,
};

const options = (values) => values.map((value) => ({label: value, value}));

export default function DiscountsTab() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const canManage = hasAdminPermission(user, "discounts.manage");
    const canView = hasAdminPermission(user, "discounts.view");
    const canViewRedemptions = hasAdminPermission(user, "discounts.redemptions.view");
    const codes = useContentTypeData(CONTENT_TYPE_UIDS.DISCOUNT_CODE);
    const plans = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION_PLAN);
    const prices = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION_PLAN_PRICE);
    const users = useContentTypeData(CONTENT_TYPE_UIDS.USER);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const [visible, setVisible] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            codes.ensure({sort: "createdAt:DESC"}, {all: true}),
            plans.ensure({sort: "sortOrder:ASC"}, {all: true}),
            prices.ensure({sort: "sortOrder:ASC", populate: {plan: true}}, {all: true}),
            users.ensure({sort: "username:ASC"}, {all: true}),
            workspaces.ensure({sort: "name:ASC"}, {all: true}),
        ]).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const planOptions = plans.items.map((plan) => ({label: plan.name, value: entityKey(plan)}));
    const priceOptions = prices.items.map((price) => ({
        label: `${price.plan?.name || "-"} / ${price.name} / ${price.billingInterval}`,
        value: entityKey(price),
    }));
    const itemTypeOptions = options([
        "subscription_new", "subscription_renewal", "subscription_upgrade",
        "subscription_downgrade", "seat_package", "addon", "ai_credit",
        "storage", "service", "adjustment",
    ]);
    const userOptions = users.items.map((user) => ({
        label: user.fullname || user.username || user.email || entityKey(user),
        value: entityKey(user),
    }));
    const workspaceOptions = workspaces.items.map((workspace) => ({label: workspace.name, value: entityKey(workspace)}));
    const steps = useMemo(() => [
        {label: translate("billing-admin-discount-step-definition")},
        {label: translate("billing-admin-discount-step-scope")},
        {label: translate("billing-admin-discount-step-limits")},
        {label: translate("billing-admin-discount-step-review")},
    ], [translate]);

    const edit = (row = null) => {
        if (!canManage) return;
        setSelected(row);
        setForm(row ? {
            ...EMPTY,
            ...row,
            startsAt: toDateTimeInput(row.startsAt),
            endsAt: toDateTimeInput(row.endsAt),
            eligiblePlans: (row.eligiblePlans || []).map(entityKey),
            eligiblePrices: (row.eligiblePrices || []).map(entityKey),
            excludedPlans: (row.excludedPlans || []).map(entityKey),
            eligibleItemTypes: Array.isArray(row.eligibleItemTypes) ? row.eligibleItemTypes : [],
            ownerUser: entityKey(row.ownerUser),
            ownerWorkspace: entityKey(row.ownerWorkspace),
        } : {...EMPTY});
        setStep(0);
        setError(null);
        setVisible(true);
    };

    const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        title: form.title.trim(),
        value: Number(form.value || 0),
        maximumDiscountAmount: form.maximumDiscountAmount == null ? null : String(form.maximumDiscountAmount),
        minimumOrderAmount: form.minimumOrderAmount == null ? null : String(form.minimumOrderAmount),
        startsAt: toISOStringOrNull(form.startsAt),
        endsAt: toISOStringOrNull(form.endsAt),
        eligiblePlans: selected
            ? relationMutationMany(form.eligiblePlans, selected.eligiblePlans)
            : {connect: form.eligiblePlans.map((documentId) => ({documentId}))},
        eligiblePrices: selected
            ? relationMutationMany(form.eligiblePrices, selected.eligiblePrices)
            : {connect: form.eligiblePrices.map((documentId) => ({documentId}))},
        excludedPlans: selected
            ? relationMutationMany(form.excludedPlans, selected.excludedPlans)
            : {connect: form.excludedPlans.map((documentId) => ({documentId}))},
        eligibleItemTypes: form.eligibleItemTypes,
        ownerUser: relationMutation(form.ownerUser, selected?.ownerUser),
        ownerWorkspace: selected
            ? relationMutation(form.ownerWorkspace, selected.ownerWorkspace)
            : form.ownerWorkspace ? {connect: [{documentId: form.ownerWorkspace}]} : {connect: []},
    };

    const save = async (event) => {
        event?.preventDefault();
        if (!canManage) return;
        setSaving(true);
        setError(null);
        try {
            if (selected) await codes.update(entityKey(selected), payload);
            else await codes.create(payload);
            setVisible(false);
            await codes.reload({sort: "createdAt:DESC"}, {all: true});
        } catch (requestError) {
            setError(requestError?.response?.data?.error?.message || requestError?.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <TabView className="rtl text-xs">
            {canView && <TabPanel header={translate("billing-admin-discount-codes")}>
                <div className="flex flex-column gap-3">
                    <SalesSectionHeader title={translate("billing-admin-discount-codes")} description={translate("billing-admin-discount-codes-description")} actions={canManage ? <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("create")} onClick={() => edit()}/> : null}/>
                    <TableCard>
                        <DataState loading={codes.loading} error={codes.error} empty={!codes.items.length}>
                            <DataTable {...RTL_DATATABLE_PROPS} value={codes.items} paginator rows={10}>
                                <Column className="text-right" field="code" header={translate("billing-admin-code")}/>
                                <Column className="text-right" field="title" header={translate("name")}/>
                                <Column className="text-right" header={translate("billing-admin-discount-value")} body={(row) => row.discountType === "percentage" ? `${row.value}%` : formatMoney(row.value, row.currency, translate)}/>
                                <Column className="text-right" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.discountCodeStatus)} value={row.discountCodeStatus}/>}/>
                                {canManage && <Column className="text-right" body={(row) => <Button className="gap-2" icon="pi pi-pencil" text onClick={() => edit(row)}/>}/>}
                            </DataTable>
                        </DataState>
                    </TableCard>
                    <MarkdownHelp contentKey="billing-admin-discount-codes-help"/>
                </div>
            </TabPanel>}
            {canViewRedemptions && <TabPanel header={translate("billing-admin-discount-redemptions")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.DISCOUNT_REDEMPTION} titleKey="billing-admin-discount-redemptions" columns={[
                    relationColumn("discountCode", "billing-admin-code"),
                    relationColumn("workspace", "sales-workspace"),
                    moneyColumn("discountAmount"),
                    statusColumn("discountRedemptionStatus"),
                    dateColumn("appliedAt"),
                ]}/>
            </TabPanel>}

        </TabView>
        <FormDialog
            visible={visible}
            title={translate(selected ? "edit" : "create")}
            description={translate("billing-admin-discount-form-description")}
            saving={saving}
            error={error}
            onHide={() => setVisible(false)}
            onSubmit={save}
            renderFooter={({formId}) => (
                <div className="rtl flex align-items-center justify-content-between gap-2 p-3 surface-50 border-top-1 border-300 border-round-bottom-2xl">
                    <Button type="button" className="text-xs" label={translate("cancel")} severity="secondary" outlined disabled={saving} onClick={() => setVisible(false)}/>
                    <div className="flex flex-row-reverse align-items-center gap-2">
                        {step < 3
                            ? <Button type="button" className="text-xs" label={translate("next")} disabled={step === 0 && (!form.code.trim() || !form.title.trim())} onClick={() => setStep((value) => value + 1)}/>
                            : <Button type="submit" form={formId} className="text-xs gap-2" label={translate("save")} icon="pi pi-save" loading={saving}/>}
                        <Button type="button" className="text-xs" label={translate("back")} outlined disabled={step === 0 || saving} onClick={() => setStep((value) => value - 1)}/>
                    </div>
                </div>
            )}
        >
                <div className="col-12">
                    <Steps model={steps} activeIndex={step} readOnly={false} onSelect={(event) => setStep(event.index)}/>
                </div>
                <div className="col-12 grid m-0 p-0 mt-3">
                    {step === 0 && <>
                        <FormField label={translate("billing-admin-code")} required><InputText className="w-full ltr text-xs" value={form.code} onChange={(event) => setForm({...form, code: event.target.value})}/></FormField>
                        <FormField label={translate("name")} required><InputText className="w-full text-xs" value={form.title} onChange={(event) => setForm({...form, title: event.target.value})}/></FormField>
                        <FormField label={translate("billing-admin-discount-type")}><Dropdown {...RTL_DROPDOWN_PROPS} value={form.discountType} options={options(["percentage", "fixed_amount", "free_period", "credit", "custom"])} onChange={(event) => setForm({...form, discountType: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-discount-value")}><InputNumber className="w-full" value={form.value} min={0} max={form.discountType === "percentage" ? 100 : undefined} useGrouping={false} onValueChange={(event) => setForm({...form, value: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-maximum-discount")}><InputNumber className="w-full" value={form.maximumDiscountAmount} min={0} useGrouping={false} onValueChange={(event) => setForm({...form, maximumDiscountAmount: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-currency")}><InputText className="w-full text-xs ltr" value={form.currency} onChange={(event) => setForm({...form, currency: event.target.value.toUpperCase()})}/></FormField>
                        <FormField label={translate("sales-status")}><Dropdown {...RTL_DROPDOWN_PROPS} value={form.discountCodeStatus} options={options(["draft", "active", "paused", "expired", "disabled"])} onChange={(event) => setForm({...form, discountCodeStatus: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-source")}><Dropdown {...RTL_DROPDOWN_PROPS} value={form.source} options={options(["manual", "campaign", "referral_reward", "support", "sales", "retention", "system", "partner"])} onChange={(event) => setForm({...form, source: event.value})}/></FormField>
                    </>}
                    {step === 1 && <>
                        <FormField className="col-12" label={translate("billing-admin-eligible-plans")}><SalesMultiSelect value={form.eligiblePlans} options={planOptions} placeholderKey="billing-admin-eligible-plans" searchPlaceholderKey="search" onChange={(event) => setForm({...form, eligiblePlans: event.value.map(entityKey).filter(Boolean)})}/></FormField>
                        <FormField className="col-12" label={translate("billing-admin-excluded-plans")}><SalesMultiSelect value={form.excludedPlans} options={planOptions} placeholderKey="billing-admin-excluded-plans" searchPlaceholderKey="search" onChange={(event) => setForm({...form, excludedPlans: event.value.map(entityKey).filter(Boolean)})}/></FormField>
                        <FormField className="col-12" label={translate("billing-admin-eligible-prices")}><SalesMultiSelect value={form.eligiblePrices} options={priceOptions} placeholderKey="billing-admin-eligible-prices" searchPlaceholderKey="search" onChange={(event) => setForm({...form, eligiblePrices: event.value.map(entityKey).filter(Boolean)})}/></FormField>
                        <FormField className="col-12" label={translate("billing-admin-eligible-item-types")}><SalesMultiSelect value={form.eligibleItemTypes} options={itemTypeOptions} placeholderKey="billing-admin-eligible-item-types" searchPlaceholderKey="search" onChange={(event) => setForm({...form, eligibleItemTypes: event.value.map(entityKey).filter(Boolean)})}/></FormField>
                        <FormField label={translate("billing-admin-owner-user")}><ClearableDropdown filter value={form.ownerUser} options={userOptions} onChange={(event) => setForm({...form, ownerUser: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-owner-workspace")}><ClearableDropdown filter value={form.ownerWorkspace} options={workspaceOptions} onChange={(event) => setForm({...form, ownerWorkspace: event.value})}/></FormField>
                        <SwitchField label={translate("billing-admin-first-purchase")} checked={form.firstPurchaseOnly} onChange={(value) => setForm({...form, firstPurchaseOnly: value})}/>
                        <SwitchField label={translate("billing-admin-new-workspace")} checked={form.newWorkspaceOnly} onChange={(value) => setForm({...form, newWorkspaceOnly: value})}/>
                        <SwitchField label={translate("billing-admin-stackable")} checked={form.stackable} onChange={(value) => setForm({...form, stackable: value})}/>
                        <SwitchField label={translate("billing-admin-auto-apply")} checked={form.autoApply} onChange={(value) => setForm({...form, autoApply: value})}/>
                    </>}
                    {step === 2 && <>
                        <FormField label={translate("billing-admin-total-usage-limit")}><InputNumber className="w-full" value={form.totalUsageLimit} min={0} onValueChange={(event) => setForm({...form, totalUsageLimit: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-workspace-limit")}><InputNumber className="w-full" value={form.perWorkspaceLimit} min={0} onValueChange={(event) => setForm({...form, perWorkspaceLimit: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-user-limit")}><InputNumber className="w-full" value={form.perUserLimit} min={0} onValueChange={(event) => setForm({...form, perUserLimit: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-minimum-order")}><InputNumber className="w-full" value={form.minimumOrderAmount} min={0} useGrouping={false} onValueChange={(event) => setForm({...form, minimumOrderAmount: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-minimum-billing-periods")}><InputNumber className="w-full" value={form.minimumBillingPeriods} min={0} onValueChange={(event) => setForm({...form, minimumBillingPeriods: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-priority")}><InputNumber className="w-full" value={form.priority} onValueChange={(event) => setForm({...form, priority: event.value})}/></FormField>
                        <FormField label={translate("billing-admin-start-date")}><InputText className="w-full ltr" type="datetime-local" value={form.startsAt} onChange={(event) => setForm({...form, startsAt: event.target.value})}/></FormField>
                        <FormField label={translate("billing-admin-end-date")}><InputText className="w-full ltr" type="datetime-local" value={form.endsAt} onChange={(event) => setForm({...form, endsAt: event.target.value})}/></FormField>
                    </>}
                    {step === 3 && <div className="col-12 surface-50 border-round p-4 flex flex-column gap-2">
                        <strong>{form.title} ({form.code})</strong>
                        <span>{translate("billing-admin-discount-value")}: {form.value} / {form.discountType}</span>
                        <span>{translate("billing-admin-eligible-plans")}: {form.eligiblePlans.length}</span>
                        <span>{translate("sales-status")}: {form.discountCodeStatus}</span>
                    </div>}
                </div>
        </FormDialog>
        </>
    );
}
